import React, { useState } from "react";
import { fetchSystemLogs } from "../../api/systemLogService";
import { predictThreats } from "../../api/anomalyPredictionService";


const Spinner = () => (
    <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
    </div>
);

const AnomalyPredictionView = () => {
    const [inputFeatures, setInputFeatures] = useState([]);
    const [predictions, setPredictions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const demoData = [
        { feature1: 10, feature2: 5, feature3: 2, timestamp: new Date().toISOString() },
        { feature1: 12, feature2: 6, feature3: 3, timestamp: new Date().toISOString() },
        { feature1: 100, feature2: 1, feature3: 0, timestamp: new Date().toISOString() },
    ];

    const buildFeaturesFromLogs = (logs) => {
        return logs.slice(0, 20).map((log, index) => {
            const level = (log.level || "").toLowerCase();
            const messageLength = log.message?.length || 0;

            return {
                feature1: level === "error" ? 100 : level === "warning" ? 50 : 10,
                feature2: messageLength,
                feature3: index,
                timestamp: log.timestamp || new Date().toISOString(),
            };
        });
    };

    const normalizeResult = (data) => {
        const raw = data?.data ?? data?.predictions ?? data;
        if (Array.isArray(raw)) return raw;
        if (raw && typeof raw === "object") return [raw];
        return [];
    };

    const getFriendlyError = (err) => {
        if (err?.response) {
            return (
                err.response.data?.message ||
                err.response.data?.error ||
                `Server returned status ${err.response.status}.`
            );
        }

        if (err?.request) {
            return "Backend is not available or CORS blocked the request. Check API server, domain, token, and endpoint.";
        }

        return err?.message || "Error running prediction.";
    };

    const runPrediction = async () => {
        setLoading(true);
        setError("");
        setPredictions([]);
        setInputFeatures([]);

        try {
            let features = demoData;

            try {
                const logs = await fetchSystemLogs();

                if (Array.isArray(logs) && logs.length > 0) {
                    features = buildFeaturesFromLogs(logs);
                }
            } catch {
                features = demoData;
            }
            setInputFeatures(features);

            const data = await predictThreats(features);
            setPredictions(normalizeResult(data));
           
        } catch (err) {
            console.error("Error running prediction:", err);
            setError(getFriendlyError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-4 bg-white rounded shadow">
            <h2 className="text-xl font-bold mb-2">Predicted Anomalies</h2>

            <p className="text-sm text-gray-600 mb-4">
                This module builds prediction features from backend SystemLog records.
                If logs are unavailable, it uses demo fallback data.
            </p>

            <button
                onClick={runPrediction}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-60"
            >
                {loading ? "Running..." : "Run Prediction"}
            </button>

            {loading && <Spinner />}

            {error && (
                <div className="mt-4 p-3 rounded border border-red-300 bg-red-50 text-red-700">
                    ⚠ {error}
                </div>
            )}

            {inputFeatures.length > 0 && (
                <div className="bg-gray-50 border rounded p-3 mt-4">
                    <h3 className="font-semibold mb-2">Input features</h3>

                    <table className="w-full text-sm border">
                        <thead>
                            <tr>
                                <th className="border p-2">Feature 1</th>
                                <th className="border p-2">Feature 2</th>
                                <th className="border p-2">Feature 3</th>
                                <th className="border p-2">Timestamp</th>
                            </tr>
                        </thead>

                        <tbody>
                            {inputFeatures.map((item, index) => (
                                <tr key={index}>
                                    <td className="border p-2">{item.feature1}</td>
                                    <td className="border p-2">{item.feature2}</td>
                                    <td className="border p-2">{item.feature3}</td>
                                    <td className="border p-2">
                                        {item.timestamp
                                            ? new Date(item.timestamp).toLocaleString()
                                            : "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {predictions.length > 0 && (
                <div className="mt-4">
                    <h3 className="font-semibold mb-2">Prediction result</h3>

                    <table className="table-auto w-full border text-sm">
                        <thead>
                            <tr className="bg-gray-100">
                                <th className="border px-4 py-2">Index</th>
                                <th className="border px-4 py-2">Is Anomaly</th>
                                <th className="border px-4 py-2">Score</th>
                                <th className="border px-4 py-2">Message</th>
                            </tr>
                        </thead>

                        <tbody>
                            {predictions.map((p, idx) => (
                                <tr key={p.index ?? idx}>
                                    <td className="border px-4 py-2">{p.index ?? idx}</td>
                                    <td className="border px-4 py-2">
                                        {p.isAnomaly ? "Yes" : "No"}
                                    </td>
                                    <td className="border px-4 py-2">
                                        {p.anomalyScore !== undefined
                                            ? Number(p.anomalyScore).toFixed(6)
                                            : p.score !== undefined
                                                ? Number(p.score).toFixed(6)
                                                : "—"}
                                    </td>
                                    <td className="border px-4 py-2">
                                        {p.message || "No additional information"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AnomalyPredictionView;